import { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import { CryptoService } from '../services/CryptoService';

/**
 * Clase para interceptar peticiones y respuestas HTTP para encriptar/desencriptar datos
 */
export class CryptoInterceptor {
  private cryptoService: CryptoService;
  
  constructor() {
    this.cryptoService = CryptoService.getInstance();
  }
  
  /**
   * Interceptor para solicitudes - encripta los datos antes de enviarlos al servidor
   * @param config - Configuración de la solicitud
   * @returns Configuración modificada con datos encriptados
   */
  public requestInterceptor = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    // Solo encriptar si hay datos
    if (config.data) {
      // Guardamos los datos originales antes de encriptarlos
      const originalData = config.data;
      
      // Encriptar los datos
      config.data = this.cryptoService.encrypt(originalData);
      
      // Asegurar que se envíe como application/json
      if (!config.headers) {
        config.headers = {} as AxiosRequestHeaders;
      }
      config.headers['Content-Type'] = 'application/json';
    }
    
    return config;
  };
  
  /**
   * Interceptor para respuestas - desencripta los datos recibidos del servidor
   * @param response - Respuesta recibida
   * @returns Respuesta con datos desencriptados
   */
  public responseInterceptor = (response: AxiosResponse): AxiosResponse => {
    
    if (!response.data) {
      return response;
    }
    
    try {
      let decryptedData = null;
      
      // Si es un objeto con la propiedad encryptedData
      if (response.data && response.data.encryptedData) {
        decryptedData = this.cryptoService.decrypt(response.data.encryptedData);
      } 
      // Si es directamente una cadena hexadecimal
      else if (typeof response.data === 'string') {
        decryptedData = this.cryptoService.decrypt(response.data);
      }
      // Si es un objeto con otra estructura
      else {
        // Intentar desencriptar la respuesta completa como último recurso
        decryptedData = this.cryptoService.decrypt(response.data);
      }
      
      if (decryptedData !== null) {
        response.data = decryptedData;
      }
      
      return response;
    } catch (error) {
      console.error('Error en el interceptor de respuesta:', error);
      return response;
    }
  };
  
  /**
   * Manejador de errores en las respuestas - intenta desencriptar mensajes de error
   * @param error - Error de la petición
   * @returns Promesa rechazada con el error
   */
  public errorInterceptor = (error: any): Promise<never> => {
    // Si hay una respuesta en el error y contiene datos encriptados
    if (error.response && error.response.data && error.response.data.encryptedData) {
      try {
        // Desencriptar los datos del error
        const decryptedData = this.cryptoService.decrypt(error.response.data.encryptedData);
        
        // Reemplazar los datos encriptados con los desencriptados
        error.response.data = decryptedData;
      } catch (decryptError) {
        console.error('Error al desencriptar el mensaje de error:', decryptError);
        // En caso de error, mantener los datos originales
      }
    }
    
    return Promise.reject(error);
  };
} 